/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA ;
 */

import { get, writable } from 'svelte/store';
import { getTenantConfiguration, getTenantRoles, getTenantCountries } from '@/services/tenantConfigurationService';
import { launchAuthoring } from '@/services/ltiService';
import { extractSubdomain } from '@/core/utils';
import { get as _get } from 'lodash';
import { rolesRequiredFeatureFlagMap } from '@/constants/roles';
import { CONTENT_MODES, supportTabs } from '@/constants/general-constants.js';

export const currentTenantStore = writable(null);

function createConfigurationStore() {
    const configurationStore = writable(null);
    let cachedCountries = null;

    return {
        get() {
            return get(configurationStore);
        },
        set: configurationStore.set,
        subscribe: configurationStore.subscribe,
        clear: () => configurationStore.set(null),
        setCurrentTenant(tenant) {
            currentTenantStore.set(tenant);
        },
        getCurrentTenant() {
            return get(currentTenantStore);
        },
        async getCountries() {
            if (cachedCountries) {
                return cachedCountries;
            }
            const countries = await getTenantCountries(this.getCurrentTenant().tenantId);
            cachedCountries = countries;

            return countries;
        },
        async updateTenantRoles() {
            const { tenantId } = this.getCurrentTenant();
            const newRoles = await getTenantRoles(tenantId);
            if (newRoles) {
                const allAvailableRoles = newRoles.filter(role => {
                    const requiredFeatureFlagName = rolesRequiredFeatureFlagMap[role.name];
                    if (requiredFeatureFlagName) {
                        return get(configurationStore)[requiredFeatureFlagName];
                    }
                    return true;
                });
                configurationStore.set({
                    ...get(configurationStore),
                    globalRoles: allAvailableRoles.filter(role => role.global)
                });
            }
        },
        isAutomaticSessionSelectionEnabled() {
            const configuration = get(configurationStore);
            return configuration?.automaticSessionSelection;
        },
        isMinimalUITestTakerEnabled() {
            const configuration = get(configurationStore);
            return configuration?.minimalUITestTaker;
        },
        isMfaEnabled() {
            return !!get(configurationStore)?.multiFactorAuthentication?.enabled;
        },
        isSelfRegistrationLinkEnabled() {
            return get(configurationStore)?.selfRegistrationLink?.enabled;
        },
        isReadBehindEnabled() {
            return get(configurationStore)?.readBehindEnabled;
        },
        isCertificationProduct() {
            return get(configurationStore)?.certificationProduct;
        },
        isDiplomaGenerationEnabled() {
            return get(configurationStore)?.diplomaGenerationEnabled;
        },
        isReportGenerationEnabled() {
            return get(configurationStore)?.reportGenerationEnabled;
        },
        isWorkingProfilesEnabled() {
            return get(configurationStore)?.workingProfiles;
        },
        getSessionsContentMode() {
            const contentMode = get(configurationStore)?.sessions?.contentMode;
            return contentMode && Object.values(CONTENT_MODES).includes(contentMode) ? contentMode : null;
        },
        getSupportTabs() {
            const tabs = get(configurationStore)?.support?.tabs;
            if (!tabs) {
                return null;
            }
            const supportedTabs = Object.values(supportTabs);
            const filteredTabs = Object.keys(tabs).filter(tab => supportedTabs.includes(tab));
            return filteredTabs.length ? filteredTabs : null;
        },
        accessUserFields(fieldName) {
            const fields = get(configurationStore)?.user?.fields;
            if (typeof fields === 'undefined') {
                return fields;
            }
            return _get(fields, fieldName);
        },
        setUserFieldsProperty(fieldName, property, value) {
            const currentUser = get(configurationStore)?.user;
            const currentFields = currentUser?.fields;
            configurationStore.set({
                user: { ...currentUser, fields: { ...currentFields, [fieldName]: { [property]: value } } }
            });
        },
        accessUserFieldsProperty(fieldName, property) {
            const field = this.accessUserFields(fieldName);
            if (typeof field === 'undefined') {
                return false;
            }

            if (typeof field[property] === 'undefined') {
                return false;
            }
            return field[property];
        },
        isUserFieldVisible(fieldName) {
            return this.accessUserFieldsProperty(fieldName, 'visible');
        },
        isUserFieldRequired(fieldName) {
            return this.accessUserFieldsProperty(fieldName, 'required');
        },
        getUserHierarchyMigrationTarget() {
            const { targetUserField, targetUserRoles } = get(configurationStore);
            if (targetUserField && targetUserRoles) {
                return {
                    targetUserField,
                    targetUserRoles
                };
            }
            return null;
        },
        async updateConfigurationData() {
            const subdomain = extractSubdomain(window.location.href);
            const { configuration = {} } = await getTenantConfiguration(subdomain);

            configuration.slugs = {
                ...configuration.slugs,
                default: {
                    session: 'matter_and_measurements',
                    testTaker: 'matter_and_measurements_test_taker',
                    dashboard: 'progress_monitoring_dashboard',
                    student: 'test_taker_report',
                    ...configuration.slugs?.default
                }
            };

            configurationStore.set(configuration);
        },
        async getContentBankUrl() {
            const configuration = get(configurationStore);
            if (this.isAuthoringSSOEnabled()) {
                const location = window.location;
                return (await launchAuthoring(location.href, `${location.origin}/logout`, location.origin))
                    .ltiLaunchLink;
            }

            if (configuration.authoringUrl) {
                return configuration.authoringUrl;
            }

            return '#';
        },
        getCurrencies() {
            const configuration = get(configurationStore);
            return {
                currencies: configuration?.currencies || [],
                defaultCurrency: configuration?.defaultCurrency || 'EUR'
            };
        },
        getSessionCardTemplates() {
            // these are html templates to customize how the session cards are displayed (in MySessions section and public sessions)
            const publicTemplate = get(configurationStore)?.sessionCardTemplates?.public;
            const privateTemplate = get(configurationStore)?.sessionCardTemplates?.private;
            return { publicTemplate, privateTemplate };
        },
        getSessionCardCertificationProductTemplates() {
            // these is a html template to customize how the session cards are displayed (in MySessions section fro Certification Product)
            const template = get(configurationStore)?.sessionCardCertificationProductTemplates;
            return template;
        },
        getUserpilotConfiguration() {
            return get(configurationStore)?.userpilot;
        },
        getAuthoringLogoutUrl() {
            return get(configurationStore)?.authoringLogoutUrl || null;
        }
    };
}

export const configurationStore = createConfigurationStore();
